 import AbstractField from "./AbstractField";
import { addSvg } from "../../../shared/utils";

const MONTHS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export default class DateField extends AbstractField {
    isInputField = true;
    labelText = '';
    changeAction = null;
    inputCallback = null;

    label(text, fancy = false) {
        this.labelText = text;
        this.fancyLabel = fancy;
        return this;
    }

    onChange(action, ...args) {
        this.changeAction = action;
        this.changeArgs = args;
        return this;
    }

    onInput(callback) {
        this.inputCallback = callback;
        return this;
    }

    make() {
        this.field = this.p.createDiv();
        if (this.labelText) {
            let label = this.fancyLabel
                ? this.p.createElement('h3', this.labelText).style('margin', '0')
                : this.p.createDiv(this.labelText);
            label.parent(this.field)
                .class('input-label');
        }
        this.parentElement ? this.field.parent(this.parentElement) : null;
        this.applyClasses();
        this.applyStyles();

        this.inputRow = this.p.createDiv()
            .parent(this.field)
            .style('display', 'flex')
            .style('gap', '4px')
            .style('justify-content', 'flex-start');

        let yearWrapper = this.p.createDiv()
            .parent(this.inputRow)
            .style('position', 'relative')
            .style('width', '5rem')
            .style('flex-shrink', '0');
        this.yearInput = this.p.createInput('', 'text')
            .parent(yearWrapper)
            .class('input-text')
            .attribute('placeholder', 'Year')
            .style('width', '100%')
            .style('margin', '0')
            .style('box-sizing', 'border-box')
            .style('padding-right', '1.5rem');
        this.yearInput.elt.oninput = () => this.onYearChange();
        this.yearInput.elt.onfocus = () => this.enableHotKeys = false;
        this.yearInput.elt.onblur = () => {
            this.enableHotKeys = true;
            this.handleChange();
        };
        this.yearInput.elt.addEventListener('keypress', (e) => {
            if (!/\d/.test(e.key) && e.key !== 'Enter') e.preventDefault();
            if (e.key === 'Enter') this.yearInput.elt.blur();
        });
        let clearBtn = this.p.createSpan('')
            .parent(yearWrapper)
            .style('position', 'absolute')
            .style('right', '4px')
            .style('top', '50%')
            .style('transform', 'translateY(-50%)')
            .style('cursor', 'pointer')
            .style('line-height', '0');
        addSvg(clearBtn, 'close-circle-fill');
        clearBtn.elt.querySelector('svg').style.width = '14px';
        clearBtn.elt.querySelector('svg').style.height = '14px';
        clearBtn.elt.querySelector('svg').style.fill = '#bbb';
        clearBtn.elt.addEventListener('click', () => {
            this.yearInput.value('');
            this.onYearChange();
            this.handleChange();
        });

        this.monthSelect = this.p.createElement('select')
            .parent(this.inputRow)
            .class('input-text')
            .style('width', 'auto')
            .style('margin', '0');
        this.populateMonths();
        this.monthSelect.elt.onchange = () => this.onMonthChange();
        this.monthSelect.hide();

        this.daySelect = this.p.createElement('select')
            .parent(this.inputRow)
            .class('input-text')
            .style('width', 'auto')
            .style('margin', '0');
        this.daySelect.elt.onchange = () => this.handleChange();
        this.daySelect.hide();

        this.initialized = true;
        return this;
    }

    populateMonths() {
        this.monthSelect.elt.innerHTML = '';
        let empty = document.createElement('option');
        empty.value = '';
        empty.textContent = '—';
        this.monthSelect.elt.appendChild(empty);

        MONTHS.forEach((name, i) => {
            let option = document.createElement('option');
            option.value = String(i + 1).padStart(2, '0');
            option.textContent = name;
            this.monthSelect.elt.appendChild(option);
        });
    }

    populateDays() {
        let current = this.daySelect.elt.value;
        this.daySelect.elt.innerHTML = '';

        let empty = document.createElement('option');
        empty.value = '';
        empty.textContent = '—';
        this.daySelect.elt.appendChild(empty);

        let year = parseInt(this.yearInput.value()) || 2000;
        let month = parseInt(this.monthSelect.elt.value);
        let daysInMonth = new Date(year, month, 0).getDate();

        for (let d = 1; d <= daysInMonth; d++) {
            let option = document.createElement('option');
            option.value = String(d).padStart(2, '0');
            option.textContent = d;
            this.daySelect.elt.appendChild(option);
        }

        if (current && parseInt(current) <= daysInMonth) {
            this.daySelect.elt.value = current;
        } else {
            this.daySelect.elt.value = '';
        }
    }

    onYearChange() {
        let year = this.yearInput.value().trim();
        if (year) {
            this.monthSelect.show();
        } else {
            this.monthSelect.elt.value = '';
            this.monthSelect.hide();
            this.daySelect.elt.value = '';
            this.daySelect.hide();
        }
    }

    onMonthChange() {
        let month = this.monthSelect.elt.value;
        if (month) {
            this.populateDays();
            this.daySelect.show();
        } else {
            this.daySelect.elt.value = '';
            this.daySelect.hide();
        }
        this.handleChange();
    }

    handleChange() {
        if (this.inputCallback) {
            this.inputCallback(this.buildDateString());
        }
        if (!this.changeAction) return;
        let value = this.buildDateString();
        this.actions.execute(this.changeAction, this.element, ...this.changeArgs.map(
            arg => typeof arg === 'function' ? arg(value) : arg
        ));
    }

    buildDateString() {
        let year = this.yearInput.value().trim();
        if (!year) return null;

        let month = this.monthSelect.elt.value;
        if (!month) return year;

        let day = this.daySelect.elt.value;
        if (!day) return `${year}-${month}`;

        return `${year}-${month}-${day}`;
    }

    populate(element) {
        this.populateFunction(element, this);
    }

    value(val) {
        if (val === undefined) return this.buildDateString();

        // Reset all fields
        this.yearInput.value('');
        this.monthSelect.elt.value = '';
        this.monthSelect.hide();
        this.daySelect.elt.value = '';
        this.daySelect.hide();

        if (!val) return this;

        let parts = val.split('-');

        this.yearInput.value(parts[0]);
        this.monthSelect.show();

        if (parts[1]) {
            this.monthSelect.elt.value = parts[1];
            this.populateDays();
            this.daySelect.show();

            if (parts[2]) {
                this.daySelect.elt.value = parts[2];
            }
        }

        return this;
    }

    showError(message) {
        if (this.errorTimeout) clearTimeout(this.errorTimeout);
        if (!this.errorDiv) {
            this.errorDiv = this.p.createDiv()
                .parent(this.field)
                .style('color', 'red')
                .style('font-size', '0.85em')
                .style('margin-top', '2px');
        }
        this.errorDiv.html(message);
        this.errorDiv.show();
        this.errorTimeout = setTimeout(() => this.errorDiv.hide(), 3000);
    }

    reset() {
        this.yearInput.value('');
        this.monthSelect.elt.value = '';
        this.monthSelect.hide();
        this.daySelect.elt.value = '';
        this.daySelect.hide();
    }
}
